import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Calendar, RefreshCw, FileSpreadsheet, Phone, User, Hash, Building2, MessageSquare } from "lucide-react";
import * as XLSX from "xlsx";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

const today = () => new Date().toISOString().slice(0, 10);
const addDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

export default function MarketingFollowups() {
  const { branch, user } = useAuth();
  const [dateFrom, setDateFrom] = useState(addDays(-14));
  const [dateTo, setDateTo] = useState(addDays(30));
  const [branchFilter, setBranchFilter] = useState("ALL");
  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dueOnly, setDueOnly] = useState(true);

  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        branch_code: branchFilter, include_archived: true, limit: 500,
      };
      // Follow-up filter lives client-side because it isn't a DB index yet.
      const { data } = await api.get("/direct-bills", { params });
      setRows(data);
    } catch (err) {
      toast.error(formatApiError(err, "Load failed"));
    } finally { setLoading(false); }
  }, [branchFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const t = today();
    return rows.filter((b) => {
      if (!b.follow_up_date) return false;
      if (b.follow_up_date < dateFrom) return false;
      if (b.follow_up_date > dateTo) return false;
      if (dueOnly && b.follow_up_date < t) return false;
      return true;
    }).sort((a, b) => (a.follow_up_date || "").localeCompare(b.follow_up_date || ""));
  }, [rows, dateFrom, dateTo, dueOnly]);

  const exportExcel = () => {
    if (filtered.length === 0) { toast.error("Nothing to export"); return; }
    const list = filtered.map((b) => ({
      "Patient": b.customer_name,
      "UHID": b.mrn || "",
      "Phone": b.customer_phone || "",
      "Branch": b.branch_code,
      "Bill No": b.bill_no,
      "Bill Date": new Date(b.generated_at).toLocaleDateString(),
      "Duration": b.duration_label || `${b.follow_up_days || 0} days`,
      "Follow-up date": b.follow_up_date,
      "Total": Number(b.total || 0),
      "Notes": b.notes || "",
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(list), "Follow-ups");
    XLSX.writeFile(wb, `Followups_${branchFilter}_${dateFrom}_to_${dateTo}.xlsx`);
    toast.success(`Exported ${list.length} follow-up${list.length === 1 ? "" : "s"}`);
  };

  const t = today();

  return (
    <div className="max-w-6xl mx-auto animate-fade-up" data-testid="marketing-followups-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-[#5C5652] mb-3 ornament-dot">
            Marketing · Follow-ups
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-forest mb-1 inline-flex items-center gap-2">
            <MessageSquare className="h-7 w-7" strokeWidth={1.5} /> Patient follow-up book
          </h1>
          <p className="text-[#5C5652]">Every direct bill with a prescription duration lands here — call the patient on the follow-up date to check refill needs.</p>
        </div>
      </div>

      <div className="bg-white border border-sand rounded-lg p-4 mb-5 grid gap-3 md:grid-cols-[1fr_1fr_1fr_auto_auto] items-end">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#5C5652] mb-1 flex items-center gap-1">
            <Building2 className="h-3.5 w-3.5" strokeWidth={1.75} /> Branch
          </label>
          <select data-testid="mkt-branch" value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)}
            className="w-full h-10 px-3 rounded-lg border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 text-sm bg-white">
            <option value="ALL">All branches</option>
            {branches.map((b) => <option key={b.code} value={b.code}>{b.code} · {b.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#5C5652] mb-1 flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" strokeWidth={1.75} /> Due from
          </label>
          <input type="date" data-testid="mkt-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="w-full h-10 px-3 rounded-lg border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 text-sm" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#5C5652] mb-1 flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" strokeWidth={1.75} /> Due to
          </label>
          <input type="date" data-testid="mkt-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="w-full h-10 px-3 rounded-lg border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 text-sm" />
        </div>
        <label className="inline-flex items-center gap-2 text-xs cursor-pointer text-[#5C5652] h-10">
          <input type="checkbox" data-testid="mkt-due-only" checked={dueOnly} onChange={(e) => setDueOnly(e.target.checked)} className="h-4 w-4 accent-[#1A4331]" />
          Hide past due
        </label>
        <div className="flex gap-2">
          <button onClick={load} data-testid="mkt-refresh" className="h-10 px-3 rounded-lg border border-sand bg-white hover:bg-[#F9F6F0] text-sm font-semibold inline-flex items-center gap-2">
            <RefreshCw className="h-4 w-4" strokeWidth={1.75} />
          </button>
          <button onClick={exportExcel} data-testid="mkt-export" className="h-10 px-3 rounded-lg bg-forest text-white text-sm font-semibold hover:bg-forest-hover inline-flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4" strokeWidth={1.75} /> Excel
          </button>
        </div>
      </div>

      <div className="bg-white border border-sand rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-sand text-[10px] uppercase tracking-wider text-[#5C5652] font-bold">
          {filtered.length} follow-up{filtered.length === 1 ? "" : "s"}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="mkt-table">
            <thead className="bg-[#F9F6F0] text-[10px] uppercase tracking-wider text-[#5C5652]">
              <tr>
                <th className="text-left px-3 py-2">Follow-up date</th>
                <th className="text-left px-3 py-2">Patient</th>
                <th className="text-left px-3 py-2">UHID</th>
                <th className="text-left px-3 py-2">Phone</th>
                <th className="text-left px-3 py-2">Duration</th>
                <th className="text-left px-3 py-2">Branch</th>
                <th className="text-left px-3 py-2">Bill</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8E3D9]">
              {loading ? (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-[#5C5652]">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-[#5C5652]">No follow-ups in this window.</td></tr>
              ) : filtered.map((b) => {
                const overdue = b.follow_up_date < t;
                return (
                  <tr key={b.id} data-testid={`mkt-row-${b.bill_no}`}>
                    <td className={`px-3 py-2 font-mono text-xs ${overdue ? "text-[#C25946] font-bold" : "text-forest font-semibold"}`}>{b.follow_up_date}</td>
                    <td className="px-3 py-2 font-semibold inline-flex items-center gap-1"><User className="h-3 w-3 text-[#5C5652]" strokeWidth={1.75} /> {b.customer_name}</td>
                    <td className="px-3 py-2 font-mono text-xs">{b.mrn || "—"}</td>
                    <td className="px-3 py-2 inline-flex items-center gap-1"><Phone className="h-3 w-3 text-[#5C5652]" strokeWidth={1.75} /> <a href={`tel:${b.customer_phone}`} className="hover:text-forest">{b.customer_phone || "—"}</a></td>
                    <td className="px-3 py-2 text-xs">{b.duration_label || `${b.follow_up_days || 0} days`}</td>
                    <td className="px-3 py-2 font-mono text-xs">{b.branch_code}</td>
                    <td className="px-3 py-2 font-mono text-xs text-[#5C5652]">{b.bill_no}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 text-[11px] text-[#5C5652]">
        Signed in as <span className="font-semibold text-forest">{user?.name}</span>. Overdue follow-ups are highlighted in red. Export to Excel for calling lists.
      </div>
    </div>
  );
}
